
import React, { useState, useEffect, useRef } from "react";
import { useAiContext } from "./AiContext";
import { useLegalAIHelper, LegalAIContextPayload } from "../hooks/useLegalAIHelper";
import { FileUploadKit } from "../components/common/FileUploadKit";
import type { AttachmentRef, AIDataSource } from "../types/ai";
import { useAuth } from "../context/AuthContext";
import type { NavKey } from "../Sidebar";
import { extractPlainTextFromFile } from "../utils/fileTextExtractor";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  context: NavKey;
};

export function AIAssistantDrawer({ isOpen, onClose, context }: Props) {
  const { activeItem, activeDraft, setAiOpen } = useAiContext();
  const { user } = useAuth();
  
  const [payload, setPayload] = useState<LegalAIContextPayload | null>(null);
  
  // Attachments State
  // 1. tempUploadedFiles: Files successfully uploaded to storage
  const [tempUploadedFiles, setTempUploadedFiles] = useState<AttachmentRef[]>([]);
  // 2. pendingLocalFiles: Files selected but not uploaded
  const [pendingLocalFiles, setPendingLocalFiles] = useState<File[]>([]);

  const [isUploading, setIsUploading] = useState(false);
  const [showApplyMenu, setShowApplyMenu] = useState(false);
  const [sourceMode, setSourceMode] = useState<AIDataSource>("attachments"); 

  useEffect(() => {
    // 1. DRAFT MODE
    if (activeDraft) {
      setPayload({
        contextType: (activeDraft.type || 'DRAFT') as any,
        contextId: "draft",
        contextTitle: activeDraft.data.title || "(草稿)",
        currentFields: activeDraft.data,
        templateText: activeDraft.data.templateContent,
        attachments: (activeDraft.data.attachments || []).map((a: any) => ({
            id: a.blobId || a.id || "draft-file",
            name: a.name,
            downloadUrl: a.url,
            storagePath: a.path || a.storagePath,
            size: a.size,
            contentType: a.contentType
        }))
      });
    } 
    // 2. EXISTING ITEM MODE
    else if (activeItem) {
      setPayload({
        contextType: (activeItem.type || 'GENERAL') as any,
        contextId: activeItem.id,
        contextTitle: activeItem.title,
        currentFields: activeItem.data,
        attachments: (activeItem.attachments || []).map((a: any) => ({
            id: a.blobId || a.id || "item-file",
            name: a.name,
            downloadUrl: a.url,
            storagePath: a.path || a.storagePath,
            size: a.size,
            contentType: a.contentType
        }))
      });
    } 
    // 3. NO CONTEXT (GENERAL DASHBOARD)
    else {
      setPayload({
          contextType: 'GENERAL',
          contextId: `gen-${Date.now()}`,
          contextTitle: '通用助理 (General Mode)',
          attachments: []
      });
    }
  }, [activeItem, activeDraft, isOpen]);

  const helper = useLegalAIHelper(payload, {
    onApplyResult: (target, content) => {
      if (activeDraft?.onApplyResult) {
        activeDraft.onApplyResult(target, content);
      } else if (activeItem?.onApplyResult) {
        activeItem.onApplyResult(target, content);
      } else {
        alert("此資料來源不支援自動寫回功能，請手動複製。");
      }
      setShowApplyMenu(false);
    }
  });

  useEffect(() => {
    if (isOpen && !helper.isOpen) helper.openHelper();
    if (!isOpen && helper.isOpen) helper.closeHelper();
  }, [isOpen]);

  useEffect(() => {
      helper.setDataSource(sourceMode);
  }, [sourceMode]);

  const handleClose = () => {
    helper.closeHelper();
    onClose();
    setAiOpen(false);
  };

  const handleFileSelect = (files: File[]) => {
      setPendingLocalFiles(prev => [...prev, ...files]);
      // Auto switch to attachments
      setSourceMode('attachments');
  };

  /**
   * IMPORTANT: We no longer upload files for the floating assistant.
   * Many environments (demo/site) don't have Storage rules configured and will throw "上傳失敗".
   * Instead we do best-effort text extraction (PDF text layer + OCR fallback for scanned PDFs)
   * and pass extractedText to /api/ai-assistant.
   */
  const prepareAttachmentsFromLocalFiles = async () => {
    if (pendingLocalFiles.length === 0) return [];

    setIsUploading(true);
    const newRefs: AttachmentRef[] = [];
    const sessionId = "ai-local-" + Date.now();

    try {
      for (let i = 0; i < pendingLocalFiles.length; i++) {
        const f = pendingLocalFiles[i];
        const extracted = await extractPlainTextFromFile(f);
        const extractedText = extracted?.ok ? String(extracted.text || "").trim() : "";

        newRefs.push({
          id: `${sessionId}-${i}`,
          name: f.name,
          downloadUrl: "", // intentionally empty (no upload)
          storagePath: "",
          size: f.size,
          contentType: f.type || "application/octet-stream",
          extractedText,
        });
      }

      setTempUploadedFiles((prev) => [...prev, ...newRefs]);
      setPendingLocalFiles([]);
      return newRefs;
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveTempFile = (index: number) => {
      setTempUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleRemovePendingFile = (index: number) => {
      setPendingLocalFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeepAnalyze = async () => {
    let finalTempFiles = [...tempUploadedFiles];
    
    // Check pending files
    if (pendingLocalFiles.length > 0) {
        try {
            const uploaded = await prepareAttachmentsFromLocalFiles();
            if (uploaded) {
                finalTempFiles = [...finalTempFiles, ...uploaded];
            }
        } catch(e) { return; } // Stop if extraction failed
    }

    helper.callAI(finalTempFiles);
  };

  // Render Result Area
  const renderResult = () => {
      if (helper.isLoading) {
          return (
              <div style={{padding: 20, textAlign: "center", color: "var(--muted)", border: "1px dashed #444", borderRadius: 8}}>
                  <div style={{fontSize: 24, marginBottom: 10}}>🤖</div>
                  <div>AI 正在思考分析中...</div>
                  <div style={{fontSize: 12, marginTop: 5}}>解析檔案內容需要 10-30 秒</div>
              </div>
          );
      }

      if (helper.errorMessage) {
          return (
              <div className="err" style={{marginBottom: 16, whiteSpace: "pre-wrap"}}>
                  <b>❌ 分析發生錯誤</b>
                  <br/>
                  {helper.errorMessage}
              </div>
          );
      }

      if (helper.responseText) {
          return (
            <div style={{ background: "rgba(255,255,255,0.05)", padding: 16, borderRadius: 8, position: "relative" }}>
                <div style={{whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.6, color: "#eee"}}>
                    {helper.responseText}
                </div>
                
                <div style={{marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 10}}>
                    <button className="smallbtn" onClick={() => { navigator.clipboard.writeText(helper.responseText); alert("已複製"); }}>
                        複製
                    </button>
                    {(activeItem || activeDraft) && (
                        <button className="smallbtn" style={{background: "#3b82f6", color: "#fff", border: "none"}} onClick={() => setShowApplyMenu(!showApplyMenu)}>
                            寫入欄位
                        </button>
                    )}
                </div>
                {showApplyMenu && (
                    <div style={{position: "absolute", bottom: "40px", right: 0, background: "#222", border: "1px solid #444", borderRadius: 6, zIndex: 10, minWidth: 140}}>
                        <div style={{padding: "8px 12px", cursor: "pointer", fontSize: 13}} onClick={() => helper.applyResultToField("summary")}>寫入摘要</div>
                        <div style={{padding: "8px 12px", cursor: "pointer", fontSize: 13}} onClick={() => helper.applyResultToField("risk")}>寫入風險</div>
                        <div style={{padding: "8px 12px", cursor: "pointer", fontSize: 13}} onClick={() => helper.applyResultToField("note")}>寫入備註</div>
                    </div>
                )}
            </div>
          );
      }

      return <div style={{padding: 20, textAlign: "center", color: "#666", fontSize: 13}}>（請上傳檔案後按「開始解析」）</div>;
  };

  if (!isOpen) return null;

  const hasAttachments = (payload?.attachments?.length || 0) + tempUploadedFiles.length + pendingLocalFiles.length > 0;

  return (
    <div 
      style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 480,
        background: "#161616", borderLeft: "1px solid var(--border)",
        boxShadow: "-5px 0 20px rgba(0,0,0,0.5)", zIndex: 2002,
        display: "flex", flexDirection: "column"
      }}
      onClick={(e) => e.stopPropagation()} 
    >
      {/* 1. Header */}
      <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", background: "#1a1d24" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <b style={{ fontSize: 16 }}>✨ AI 法律助手 (Pro)</b>
          <button onClick={handleClose} style={{ background: "transparent", border: "none", color: "#ccc", fontSize: 20, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ fontSize: 12, color: "var(--ok)", marginTop: 4 }}>
          {payload?.contextTitle || "通用模式"}
        </div>
      </div>

      {/* 2. Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--border)", background: "#111", overflowX: "auto" }}>
        <TabButton label="摘要" active={helper.activeMode==="summary"} onClick={() => helper.setActiveMode("summary")} />
        <TabButton label="分析" active={helper.activeMode==="analysis"} onClick={() => helper.setActiveMode("analysis")} />
        <TabButton label="風險" active={helper.activeMode==="risk"} onClick={() => helper.setActiveMode("risk")} />
        <TabButton label="決策" active={helper.activeMode==="decision"} onClick={() => helper.setActiveMode("decision")} />
        <TabButton label="草擬" active={helper.activeMode==="draft"} onClick={() => helper.setActiveMode("draft")} />
      </div>

      {/* 3. Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        
        {/* Source Selection */}
        <div style={{marginBottom: 20}}>
            <div className="label" style={{marginBottom: 8}}>資料來源</div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                <RadioOption label="僅使用補充說明/上傳" value="notesOnly" selected={sourceMode} onChange={setSourceMode} />
                <RadioOption label="附件檔案分析 (推薦)" value="attachments" selected={sourceMode} onChange={setSourceMode} />
            </div>
        </div>

        {/* User Prompt */}
        <div className="field" style={{ marginBottom: 20 }}>
          <label className="label">補充指令 / Prompt</label>
          <textarea 
            className="input" 
            rows={3} 
            placeholder="例如：請幫我檢查賠償上限..." 
            value={helper.notes} 
            onChange={e => helper.setNotes(e.target.value)} 
          />
        </div>

        {/* File Upload */}
        <div className="section" style={{marginBottom: 20, padding: 12, background: 'rgba(255,255,255,0.03)'}}>
            <FileUploadKit 
               label="上傳分析檔案"
               accept={[".docx", ".doc", ".txt", ".md", ".pdf"]}
               multiple={true} 
               onFilesSelected={handleFileSelect}
               existingFiles={tempUploadedFiles as any}
               rawFiles={pendingLocalFiles}
               onRemoveFile={(f, idx) => handleRemoveTempFile(idx)}
               onRemoveRawFile={(idx) => handleRemovePendingFile(idx)}
               disabled={isUploading}
               description="支援 Word, PDF, Text"
            />
            {isUploading && <div style={{fontSize: 11, color: '#fbbf24', marginTop: 4}}>上傳中...</div>}
            
            {pendingLocalFiles.length > 0 && !isUploading && (
                <div style={{fontSize:11, color:'#fbbf24', marginTop:4}}>
                    還有 {pendingLocalFiles.length} 個檔案等待上傳，按下「開始解析」將自動上傳。
                </div>
            )}
        </div>

        {/* Action Button */}
        <button 
          className="btn" 
          onClick={handleDeepAnalyze} 
          disabled={helper.isLoading || isUploading || (!hasAttachments && !helper.notes)}
          style={{
            marginBottom: 24,
            background: 'linear-gradient(135deg, #6366f1, #a855f7)', 
            color: 'white', 
            border: 'none',
            fontWeight: 600,
            cursor: (helper.isLoading || isUploading) ? 'not-allowed' : 'pointer',
            opacity: (helper.isLoading || isUploading) ? 0.7 : 1
          }}
        >
          {isUploading ? "檔案上傳中..." : helper.isLoading ? "AI 思考中..." : "開始解析"}
        </button>

        {/* Result Area */}
        {renderResult()}
      </div>
      
      {/* Footer */}
      <div style={{padding: '8px 20px', borderTop: '1px solid #333', fontSize: 10, color: '#555'}}>
          Build: v4.9.5-unified-ai
      </div>
    </div>
  );
}

function TabButton({ label, active, onClick }: any) {
  return (
    <button onClick={onClick} style={{ flex: "0 0 auto", minWidth: 60, padding: "12px 16px", background: "transparent", border: "none", borderBottom: active ? "2px solid #a855f7" : "2px solid transparent", color: active ? "#fff" : "var(--muted)", cursor: "pointer", fontSize: 13, fontWeight: active ? "bold" : "normal" }}>{label}</button>
  );
}

function RadioOption({ label, value, selected, onChange }: any) {
    return (
        <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: selected === value ? '#fff' : '#aaa'}}>
            <input type="radio" name="aiSource" value={value} checked={selected === value} onChange={() => onChange(value)} />
            {label}
        </label>
    );
}
